import { writable } from 'svelte/store';
import { supabase } from '$lib/supabase';
import { authStore } from './authStore';

interface FollowState {
  followedChannels: string[];
  latestVideos: any[];
  liveStreams: any[];
  upcomingChannels: any[];
  isLoading: boolean;
  error: string | null;
}

export const followStore = writable<FollowState>({
  followedChannels: [],
  latestVideos: [],
  liveStreams: [],
  upcomingChannels: [],
  isLoading: true,
  error: null
});

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1秒

async function fetchWithRetry(fn: () => Promise<any>, retries = MAX_RETRIES): Promise<any> {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return fetchWithRetry(fn, retries - 1);
    }
    throw error;
  }
}

async function fetchFollowedChannels(userId: string) {
  return await fetchWithRetry(async () => {
    const { data, error } = await supabase
      .from('follows')
      .select('channel_id, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching followed channels:', error);
      return [];
    }
    return data.map(item => item.channel_id);
  });
}

async function fetchVideosData(channelIds: string[]) {
  return await fetchWithRetry(async () => {
    if (!channelIds.length) return null;

    const { data, error } = await supabase
      .from('video')
      .select('*')
      .limit(800)
      .in('channel_id', channelIds)
      .order('published_at', { ascending: false });

    if (error) {
      console.error('Error fetching videos:', error);
      throw error;
    }
    return data;
  });
}

function filterUpcomingVideos(videos: any[]) {
  return videos.filter(video => {
    if (!video.id || !video.scheduled_start_time) return false;
    const now = new Date();
    const scheduledTime = new Date(video.scheduled_start_time);
    const maxFutureTime = new Date(now.getTime() + 72 * 60 * 60 * 1000);
    return scheduledTime > now && scheduledTime <= maxFutureTime;
  });
}

export async function initializeFollowData() {
  followStore.update(state => ({ ...state, isLoading: true, error: null }));

  try {
    const unsubscribe = authStore.subscribe(async ($authStore) => {
      if (!$authStore.loading) {
        if ($authStore.user) {
          try {
            const followedChannels = await fetchFollowedChannels($authStore.user.id);
            if (followedChannels.length > 0) {
              const videosData = await fetchVideosData(followedChannels);
              if (videosData) {
                followStore.set({
                  followedChannels,
                  latestVideos: videosData.filter(video => video.live_broadcast_content === 'none'),
                  liveStreams: videosData.filter(video => video.live_broadcast_content === 'live'),
                  upcomingChannels: filterUpcomingVideos(videosData),
                  isLoading: false,
                  error: null
                });
              }
            } else {
              followStore.set({
                followedChannels: [],
                latestVideos: [],
                liveStreams: [],
                upcomingChannels: [],
                isLoading: false,
                error: null
              });
            }
          } catch (error) {
            console.error('Error initializing follow data:', error);
            followStore.update(state => ({
              ...state,
              isLoading: false,
              error: 'データの取得に失敗しました。再読み込みしてください。'
            }));
          }
        } else {
          followStore.set({
            followedChannels: [],
            latestVideos: [],
            liveStreams: [],
            upcomingChannels: [],
            isLoading: false,
            error: null
          });
        }
      }
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error in initializeFollowData:', error);
    followStore.update(state => ({
      ...state,
      isLoading: false,
      error: 'データの初期化に失敗しました。再読み込みしてください。'
    }));
  }
} 
